import {
  Checkbox,
  Drawer,
  Flex,
  Image,
  Popconfirm,
  Tag,
  Typography,
} from 'antd';
import {
  ActionButtonWrapper,
  Container,
  Description,
  ImageContainer,
  IssueName,
} from './style';
import {
  Cause,
  Issue,
  QueryType,
  SuggestionStatus,
  YesOrNo,
} from '../../types';
import { useTranslation } from 'react-i18next';
import Icon from '@ant-design/icons';
import { SVGDelete, SVGEdit } from '../../images';
import SuggestionTag from './suggestionTag';
import { useState } from 'react';
import SuggestionReviewDrawer from '../suggestionReviewDrawer';
import { fetchIssues } from '../../slices/issues';
import { useAppDispatch } from '../../store';
import { fetchTemplateIssues } from '../../slices/templates';
import IssueForm from '../issueForm';

export interface IssueCardProps {
  issue: Issue | Cause;
  showEdit?: boolean;
  showDelete?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  checked?: boolean;
  checkable?: boolean;
  showSuggestionStatus?: boolean;
  onCheck?: (checked: boolean) => void;
  showLinkedBadge?: boolean;
  isLinked?: boolean;
}

const IssueCard = ({
  issue,
  showEdit,
  showDelete,
  onEdit,
  onDelete,
  checkable,
  checked,
  onCheck,
  showSuggestionStatus,
  showLinkedBadge,
  isLinked,
}: IssueCardProps) => {
  const { t } = useTranslation();
  const id = 'issueID' in issue ? issue.issueID : issue.causeID;
  const [suggestionReviewDrawerVisible, setSuggestionReviewDrawerVisible] =
    useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);

  const dispatch = useAppDispatch();

  const handleSuggestionReviewDrawerClose = (isSubmitted?: boolean) => {
    setSuggestionReviewDrawerVisible(false);
    if (isSubmitted) {
      dispatch(fetchTemplateIssues({ templateId: String(issue.templateID) }));
    }
  };

  const handleClick = (e: any) => {
    if (onCheck) {
      return;
    }
    if (issue.suggestionStatus === SuggestionStatus.SUBMITTED) {
      setSuggestionReviewDrawerVisible(true);
    } else {
      setShowDetailsDrawer(true);
    }
  };

  return (
    <Flex flex={1} align="center">
      <Container
        className={`${checked ? 'checked' : ''} ${checkable ? 'checkable' : ''}`}
        onClick={() => onCheck && onCheck(!checked)}
      >
        <Flex gap={8} onClick={handleClick}>
          {checkable && (
            <Checkbox
              checked={checked}
              onChange={(e) => onCheck && onCheck(e.target.checked)}
            ></Checkbox>
          )}
          <ImageContainer>
            <Image
              src={issue.images?.[0]?.url}
              preview={false}
              fallback="/placeholder.png"
            />
          </ImageContainer>
          <Flex vertical flex={1}>
            <IssueName>{issue.name}</IssueName>
          </Flex>
          {showSuggestionStatus && issue.suggestionStatus && (
            <SuggestionTag status={issue.suggestionStatus} />
          )}
          {showLinkedBadge && isLinked && (
            <div>
              <Tag color="cyan" bordered={false}>
                Linked in Solution Grid
              </Tag>
            </div>
          )}
        </Flex>
        {issue.description && <Description>{issue.description}</Description>}
        {showSuggestionStatus && (
          <Flex gap={16}>
            {issue.isSuggestion === YesOrNo.YES && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Submitted By: {issue?.suggestionSubmitterName || '-'} {'    '}{' '}
                at {issue?.suggestionSubmittedTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
            {issue.suggestionStatus === SuggestionStatus.APPROVED && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Accepted By: {issue?.suggestionReviewerName || '-'} {'    '} at{' '}
                {issue?.suggestionReviewTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
            {issue.suggestionStatus === SuggestionStatus.REJECTED && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Rejected By: {issue?.suggestionReviewerName || '-'} {'    '} at{' '}
                {issue?.suggestionReviewTime?.replace('T', ' ') || '-'}
              </Typography.Text>
            )}
          </Flex>
        )}
      </Container>
      <Flex gap={8}>
        {showEdit && (
          <ActionButtonWrapper
            onClick={(e) => onEdit?.(id)}
            style={{ cursor: 'pointer' }}
            title={t('edit')}
          >
            <Icon component={SVGEdit} />
          </ActionButtonWrapper>
        )}

        {showDelete && (
          <Popconfirm
            placement="bottom"
            title={`Are you sure you want to delete ${issue.name}`}
            okText="Yes"
            cancelText="No"
            onConfirm={(e) => onDelete?.(id)}
          >
            <ActionButtonWrapper
              style={{ cursor: 'pointer' }}
              title={t('delete')}
            >
              <Icon component={SVGDelete} />
            </ActionButtonWrapper>
          </Popconfirm>
        )}
      </Flex>
      <SuggestionReviewDrawer
        visible={suggestionReviewDrawerVisible}
        onClose={handleSuggestionReviewDrawerClose}
        queryType={QueryType.ISSUE}
        data={issue}
      />
      <Drawer
        title={`Issue-${issue?.name || ''}`}
        closable
        onClose={() => setShowDetailsDrawer(false)}
        open={showDetailsDrawer}
        width={800}
        footer={null}
      >
        <IssueForm issue={issue as Issue} readOnly />
      </Drawer>
    </Flex>
  );
};

export default IssueCard;
